import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import { setViewedProperty } from "store";
import { updateViewCount } from "services";

interface Property {
  uuid: string;
  view_count: number;
}

interface RootState {
  properties: {
    viewed_properties: string[];
  };
}

export function usePropertyViewCount({ property }: { property?: Property }) {
  const dispatch = useDispatch();

  const viewedProperties = useSelector(
    (state: RootState) => state.properties.viewed_properties,
  );
  const [viewCount, setViewCount] = useState<number>(0);

  const hasIncrementedRef = useRef(false);

  const incrementViewCount = useCallback(
    async (uuid: string, currentCount: number) => {
      if (!uuid) return;

      if (!viewedProperties) return;
      if (viewedProperties.includes(uuid)) return;

      const nextCount = currentCount + 1;

      dispatch(setViewedProperty([...viewedProperties, uuid]));
      setViewCount(nextCount);

      try {
        await updateViewCount({
          uuid,
          count: nextCount,
        });
      } catch (error) {
        console.error("updateViewCount failed", error);
      }
    },
    [dispatch, viewedProperties],
  );

  useEffect(() => {
    if (!property) return;
    if (hasIncrementedRef.current) return;

    setViewCount(property.view_count);
    incrementViewCount(property.uuid, property.view_count);

    hasIncrementedRef.current = true;
  }, [property?.uuid, property?.view_count, incrementViewCount, property]);

  return viewCount;
}
